'use client';

/**
 * ChartContainer Component
 *
 * Container for charts with loading and error states.
 * Provides consistent loading/error UX across all chart types.
 *
 * @example
 * ```tsx
 * <ChartContainer
 *   loading={isLoading}
 *   error={error?.message}
 *   title="Revenue Trend"
 * >
 *   <LineChart data={data} />
 * </ChartContainer>
 * ```
 */

import Box, { type BoxProps } from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getNeonStyles, type NeonAccent, neonAccents } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transientOptions } from '../../utils/styledUtils';
import { LoadingOverlay } from '../LoadingOverlay';
import { ShimmerLoader } from '../ShimmerLoader';

// ============================================================================
// Types
// ============================================================================

export interface ChartContainerProps extends Omit<BoxProps, 'title'> {
  /**
   * Chart title
   */
  title?: string;

  /**
   * Chart subtitle/description
   */
  subtitle?: string;

  /**
   * Loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Error message
   */
  error?: string | null;

  /**
   * Empty state message (shown when no data)
   */
  emptyMessage?: string;

  /**
   * Whether data is empty
   * @default false
   */
  isEmpty?: boolean;

  /**
   * Chart height
   * @default 300
   */
  height?: number;

  /**
   * Optional neon accent styling
   */
  neonAccent?: NeonAccent;

  /**
   * Enable glassmorphism background
   * @default true
   */
  glassBackground?: boolean;

  /**
   * Header action (e.g., menu button)
   */
  headerAction?: React.ReactNode;

  /**
   * Children (chart content)
   */
  children: React.ReactNode;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $glassBackground: boolean;
}

const StyledContainer = styled(
  Box,
  transientOptions,
)<StyledContainerProps>(({ theme, $isDark, $neonAccent, $glassBackground }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonColors = $neonAccent ? neonAccents[$neonAccent] : null;

  return {
    position: 'relative',
    borderRadius: 16,
    padding: theme.spacing(2),
    overflow: 'hidden',

    ...($glassBackground && {
      background: neonColors ? ($isDark ? neonColors.bg : neonColors.bgLight) : glass.background,
      border: `1px solid ${neonColors ? ($isDark ? neonColors.border : neonColors.borderLight) : glass.border}`,
      backdropFilter: glass.backdropBlur,
      boxShadow: glass.shadow,
    }),
  };
});

const overlayBaseSx = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 5,
  borderRadius: 4, // 4 * 4px = 16px — matches StyledContainer surface
} as const;

// ============================================================================
// Component
// ============================================================================

export const ChartContainer: React.FC<ChartContainerProps> = ({
  children,
  title,
  subtitle,
  loading = false,
  error = null,
  emptyMessage,
  isEmpty = false,
  height = 300,
  neonAccent,
  glassBackground = true,
  headerAction,
  sx,
  ...props
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();
  const resolvedEmptyMessage = emptyMessage ?? t('chart.noData');

  return (
    <StyledContainer $isDark={isDark} $neonAccent={neonAccent} $glassBackground={glassBackground} sx={sx} {...props}>
      {/* Header */}
      {(title || subtitle || headerAction) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box>
            {title && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {headerAction && <Box>{headerAction}</Box>}
        </Box>
      )}

      {/* Chart Content */}
      <Box
        sx={{
          position: 'relative',
          height,
          minHeight: height,
        }}
      >
        {/* Actual Chart */}
        <Box
          sx={{
            height: '100%',
            opacity: loading || error || isEmpty ? 0.3 : 1,
            transition: 'opacity 200ms ease',
          }}
        >
          {children}
        </Box>

        {/* Loading State */}
        <LoadingOverlay
          visible={!!loading}
          message={t('chart.loading')}
          neonAccent={neonAccent}
          size={20}
          borderRadius={16}
          spinner={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 80 }}>
                {[
                  { w: 24, h: '40%' },
                  { w: 24, h: '65%' },
                  { w: 24, h: '50%' },
                  { w: 24, h: '80%' },
                  { w: 24, h: '55%' },
                ].map((bar, i) => (
                  <ShimmerLoader key={i} variant="rounded" width={bar.w} height={bar.h} />
                ))}
              </Box>
              <CircularProgress
                size={20}
                sx={{ color: neonAccent ? getNeonStyles(neonAccent, isDark).base : undefined }}
              />
            </Box>
          }
        />

        {/* Error State */}
        {error && !loading && (
          <Box
            sx={{
              ...overlayBaseSx,
              bgcolor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 24,
                  color: theme.palette.error.main,
                }}
              >
                !
              </Typography>
            </Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: theme.palette.error.main,
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {t('chart.loadFailed')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                maxWidth: 280,
              }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {isEmpty && !loading && !error && (
          <Box
            sx={{
              ...overlayBaseSx,
              bgcolor: isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 32,
                  opacity: 0.3,
                }}
              >
                O
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {resolvedEmptyMessage}
            </Typography>
          </Box>
        )}
      </Box>
    </StyledContainer>
  );
};

ChartContainer.displayName = 'ChartContainer';

export default ChartContainer;
